import type { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Users,
  GitBranch,
  Video,
  Target,
  Languages,
  Shield,
  Mail,
  Building2,
  BarChart3,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useB2BView } from '@contexts/B2BViewContext';
import { landingPrimaryCtaMd, landingSectionY } from './landingCtaStyles';

/**
 * Feature grid — Dripify-inspired non-uniform tile layout.
 * Wide tiles (colSpan 2) showcase flagship capabilities; normal tiles fill the rest.
 * Grid is 3 columns on lg; 2 on md; 1 on mobile.
 *
 * Row layout (3-col grid):
 *   Row 1:  [candidates · span-2]  [pipeline · span-1]
 *   Row 2:  [ai-interviews · span-1]  [rolefit · span-2]
 *   Row 3:  [multilingual · span-1]  [rbac · span-1]  [email · span-1]
 *   Row 4:  [agency · span-2]  [analytics · span-1]
 */

type Feature = {
  icon: LucideIcon;
  title: string;
  desc: string;
  iconClass: string;
  /** Very light diagonal wash — distinct per card */
  tileSurface: string;
  /** Span 2 of 3 grid columns on lg to create a wide prominent tile */
  wide?: boolean;
};

const ALL_FEATURES: Feature[] = [
  {
    icon: Users,
    title: 'Candidate management',
    desc: 'Profiles, timeline, notes, and AI-parsed resumes — one record per person, with full activity history and document storage.',
    iconClass: 'text-violet-600 dark:text-violet-400',
    tileSurface:
      'bg-gradient-to-br from-violet-50/95 via-purple-50/60 to-fuchsia-50/35 dark:from-violet-950/35 dark:via-purple-950/25 dark:to-fuchsia-950/15',
    wide: true,
  },
  {
    icon: GitBranch,
    title: 'Pipeline & applications',
    desc: 'Stages, audit trail, bulk actions, and email tied to the same spine.',
    iconClass: 'text-indigo-600 dark:text-indigo-400',
    tileSurface:
      'bg-gradient-to-br from-indigo-50/95 via-slate-50/70 to-blue-50/40 dark:from-indigo-950/35 dark:via-slate-900/30 dark:to-blue-950/20',
  },
  {
    icon: Video,
    title: 'Live AI interviews',
    desc: 'Structured sessions, rubrics, and transcripts — video or voice when you need scale.',
    iconClass: 'text-pink-600 dark:text-pink-400',
    tileSurface:
      'bg-gradient-to-br from-pink-50/95 via-rose-50/55 to-orange-50/30 dark:from-pink-950/30 dark:via-rose-950/22 dark:to-orange-950/15',
  },
  {
    icon: Target,
    title: 'Explainable Role-Fit',
    desc: 'JD-weighted match with skills, gaps, and signals you can defend to clients. Bulk compare across your entire shortlist in one view.',
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    tileSurface:
      'bg-gradient-to-br from-emerald-50/95 via-teal-50/55 to-cyan-50/35 dark:from-emerald-950/32 dark:via-teal-950/22 dark:to-cyan-950/15',
    wide: true,
  },
  {
    icon: Languages,
    title: 'Multilingual',
    desc: 'Interview prompts and candidate flows in the languages your regions need.',
    iconClass: 'text-sky-600 dark:text-sky-400',
    tileSurface:
      'bg-gradient-to-br from-sky-50/95 via-cyan-50/50 to-blue-50/30 dark:from-sky-950/32 dark:via-cyan-950/22 dark:to-blue-950/15',
  },
  {
    icon: Shield,
    title: 'RBAC & Keycloak',
    desc: 'Role-based access and SSO-friendly auth — each audience sees the right data.',
    iconClass: 'text-blue-700 dark:text-blue-400',
    tileSurface:
      'bg-gradient-to-br from-blue-50/95 via-indigo-50/45 to-violet-50/25 dark:from-blue-950/30 dark:via-indigo-950/22 dark:to-violet-950/12',
  },
  {
    icon: Mail,
    title: 'Email sequences',
    desc: 'Nurture and follow-ups tied to pipeline stages — less inbox gymnastics.',
    iconClass: 'text-rose-600 dark:text-rose-400',
    tileSurface:
      'bg-gradient-to-br from-rose-50/95 via-pink-50/50 to-red-50/25 dark:from-rose-950/28 dark:via-pink-950/20 dark:to-red-950/12',
  },
  {
    icon: Building2,
    title: 'Agency & in-house',
    desc: 'One product spine for agency desks and employer teams — client accounts, multi-desk billing, and role-specific views baked in.',
    iconClass: 'text-amber-700 dark:text-amber-400',
    tileSurface:
      'bg-gradient-to-br from-amber-50/95 via-yellow-50/45 to-orange-50/28 dark:from-amber-950/28 dark:via-yellow-950/18 dark:to-orange-950/12',
    wide: true,
  },
  {
    icon: BarChart3,
    title: 'Analytics',
    desc: 'Funnel and activity signals — throughput, bottlenecks, and team load in one view.',
    iconClass: 'text-teal-600 dark:text-teal-400',
    tileSurface:
      'bg-gradient-to-br from-teal-50/95 via-emerald-50/45 to-green-50/28 dark:from-teal-950/28 dark:via-emerald-950/20 dark:to-green-950/12',
  },
];

function FeatureTile({
  feature,
  index,
  className = '',
}: {
  feature: Feature;
  index: number;
  className?: string;
}) {
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.24) }}
      className={`flex flex-col text-left ${feature.tileSurface} rounded-3xl border border-gray-200/90 p-6 shadow-sm dark:border-gray-700/80 ${className}`}
    >
      <Icon className={`mb-6 h-11 w-11 shrink-0 ${feature.iconClass}`} strokeWidth={1.5} aria-hidden />
      <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">{feature.title}</h3>
      <p className="flex-1 text-[15px] leading-relaxed text-slate-600 dark:text-gray-400">{feature.desc}</p>
      <Link
        to="/business/demo"
        className="mt-8 inline-flex items-center text-[15px] font-medium text-violet-700 transition-colors hover:text-indigo-600 dark:text-violet-400 dark:hover:text-violet-300"
      >
        Learn more
        <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
      </Link>
    </motion.div>
  );
}

export function B2BPlatformCenter() {
  const { surface } = useB2BView();

  return (
    <section
      id="features"
      data-b2b-surface={surface}
      className={`scroll-mt-28 bg-transparent ${landingSectionY}`}
    >
      <div className="p-4">
        <div className="mx-auto max-w-screen-xl">
          <div className="mx-auto max-w-3xl text-center">
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8 text-3xl font-bold leading-[40px] text-slate-900 dark:text-white md:mb-10 md:text-4xl md:!leading-[45px]"
            >
              Powerful features to{' '}
              <span className="whitespace-nowrap text-indigo-700 dark:text-indigo-400">boost your hiring</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="text-base leading-relaxed text-slate-600 dark:text-gray-400"
            >
              Discover how GoGrabJob can transform the way your team hires — pipeline, AI screening, explainable fit, and
              client-ready workflows in one workspace.
            </motion.p>
          </div>

          {/*
           * Non-uniform grid — wide tiles span 2 of 3 columns on lg.
           * On md: wide tiles fill both columns; on mobile: all tiles stack.
           */}
          <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-6 md:max-w-none md:grid-cols-2 md:gap-7 lg:grid-cols-3 lg:gap-8">
            {ALL_FEATURES.map((f, i) => (
              <FeatureTile
                key={f.title}
                feature={f}
                index={i}
                className={f.wide ? 'md:col-span-2 lg:col-span-2' : ''}
              />
            ))}
          </div>

          <div className="mt-10 text-center md:mt-12">
            <Link
              to="/business/demo"
              className={landingPrimaryCtaMd}
            >
              Request a demo
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
